/// <reference path="./react-table.d.ts" />

import type {
  Column,
  ColumnDef,
  ColumnPinningState,
  ColumnResizeMode,
  ColumnSort,
  ExpandedState,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useVirtual } from 'react-virtual';
import AggregateDropdown from './AggregateDropdown';
import { Table } from './Table';
import styles from './Table.module.scss';

// A simple component to display aggregate values without dropdown functionality
export const AggregateValue = ({
  column,
  rows,
  selectedFunction,
  rowSelection,
  aggregateFunctions,
  alignRight,
}: {
  column: any;
  rows: any[];
  selectedFunction: string;
  rowSelection: RowSelectionState;
  aggregateFunctions: { [key: string]: (rows: any[], column: any, rowSelection: any) => string };
  alignRight?: boolean;
}) => {
  const result = React.useMemo(() => {
    const aggregateFunction = aggregateFunctions[selectedFunction];
    return aggregateFunction(rows, column, rowSelection);
  }, [selectedFunction, rows, column, aggregateFunctions, rowSelection]);

  return (
    <div
      style={{
        textAlign: alignRight ? 'right' : 'left',
        padding: '4px 4px',
        fontSize: '0.9em',
      }}
    >
      {result}
    </div>
  );
};

const EMPTY_SORT: ColumnSort[] = [];
const EMPTY_COLUMN_PINNING: ColumnPinningState = {};

type DataTableProps<T> = {
  /** Data for each row */
  data: T[];
  /**
   * Column definitions and row mapping of the table, often defined via `columnHelper`
   * @todo Figure out proper typings, this isn't right, see `MemoryTable`.
   * @link
   */
  columns: ColumnDef<T, any>[];
  /** Resize columns live (`onChange`) or after resize is done (`onEnd`) */
  columnResizeMode?: ColumnResizeMode;
  /** Manually specify the column order. If not set, the order of column definitions will be used */
  columnOrder?: Array<string>;
  /** Sort by... */
  sorting?: ColumnSort[];
  /** Fires when sorting changes */
  onSortingChange?: OnChangeFn<SortingState>;
  /** When true, the table will assume the data is already sorted, use it for server-side sorting */
  manualSorting?: boolean;
  /** Controls column visibility */
  columnVisibility?: VisibilityState;
  /** When column visiblity changes  */
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  /** Controls column visibility */
  columnPinning?: ColumnPinningState;
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>;
  /** Resolve a unique id for any given row. If not specified Tanstack will default to row indexes. */
  getRowId?: (row: T) => string;
  /** Make the table header sticky */
  stickyHeader?: boolean;
  /** Make the table footer sticky */
  stickyFooter?: boolean;
  /**
   * Enable virtualization of table. Defaults to `false`.
   * @note You MUST set `virtualContainerHeight` for this to work.
   * @see `virtualRowSize` */
  virtualization?: boolean;
  /** Virtual area height */
  virtualContainerHeight?: number;
  /** (Generous) size of the virtual rows. Defaults to 50px. */
  virtualRowSize?: number;
  /** Enable debugging to console (useful for perf debugging) */
  debug?: boolean;
  /** Enables row selection */
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);

  /** Selected rows */
  rowSelection?: RowSelectionState;
  /** Callback when row selection changes */
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  /** Callback for row click */
  onRowClick?: (id: string, event: React.MouseEvent<HTMLElement>) => void;
  /** row expansion */
  enableRowExpansion?: boolean;
  /** expanded rows */
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  getSubRows?: (row: T) => T[];
  fixedLayout?: boolean;
  hideFooter?: boolean;
  zebraStripes?: boolean;
  verticalBorders?: boolean;
  aggregateFooter?: boolean;
  selectedAggregateFunctions?: { [key: string]: string };
  aggregateFunctions?: {
    [key: string]: (rows: any[], column: Column<any>, rowSelection: RowSelectionState) => string;
  };
  handleAggregateSelection?: (columnId: string, value: any) => void;
  getRowCanExpand?: (row: Row<T>) => boolean;
  /** Array of column IDs to highlight */
  highlightedColumns?: string[];
  /** Array of row IDs to highlight */
  highlightedRows?: string[];
};

export const DataTable = <T extends {}>({
  data,
  columns,
  columnResizeMode = 'onChange',
  columnOrder,
  stickyHeader = true,
  stickyFooter = false,
  // Sorting
  sorting = EMPTY_SORT, // VERY important that data is stable or else re-rendering gets crazy
  onSortingChange,
  manualSorting,
  // Column pinning
  columnPinning = EMPTY_COLUMN_PINNING,
  onColumnPinningChange,
  // Row selection & click
  getRowId,
  rowSelection,
  enableRowSelection,
  onRowSelectionChange,
  onExpandedChange,
  enableRowExpansion,
  getSubRows,
  onRowClick,
  // Visibility
  columnVisibility,
  onColumnVisibilityChange,
  // Virtualization
  virtualContainerHeight,
  virtualization = false,
  virtualRowSize = 35,
  debug = true,
  fixedLayout,
  hideFooter,
  zebraStripes,
  verticalBorders,
  aggregateFooter = false,
  selectedAggregateFunctions,
  aggregateFunctions,
  handleAggregateSelection,
  expanded,
  // Highlighting
  highlightedColumns = [],
  highlightedRows = [],
}: DataTableProps<T>) => {
  const table = useReactTable({
    columnResizeMode,
    data,
    columns,
    state: {
      sorting,
      columnPinning,
      columnVisibility,
      columnOrder,
      rowSelection,
      expanded,
    },
    getRowId,
    enableRowSelection,
    manualSorting,
    onRowSelectionChange,
    onSortingChange,
    onColumnVisibilityChange,
    onColumnPinningChange,
    getCoreRowModel: getCoreRowModel(),
    onExpandedChange,
    getSubRows: getSubRows || (() => []),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: debug,
    debugHeaders: debug,
    debugColumns: debug,
  });

  // Calculate cumulative offsets for pinned columns
  const leftLeafColumns = table.getLeftLeafColumns();
  const rightLeafColumns = table.getRightLeafColumns();

  const { leftOffsets, rightOffsets } = useMemo(() => {
    let left = 0;
    const leftOffsets: Record<string, number> = {};
    for (const column of leftLeafColumns) {
      leftOffsets[column.id] = left;
      left += column.getSize();
    }

    let right = 0;
    const rightOffsets: Record<string, number> = {};
    for (const column of rightLeafColumns.slice().reverse()) {
      rightOffsets[column.id] = right;
      right += column.getSize();
    }
    return { leftOffsets, rightOffsets };
  }, [leftLeafColumns, rightLeafColumns]);

  const handleRowClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const rowId = event.currentTarget.dataset['rowId']!;
      console.log('rowId', rowId);
      if (enableRowExpansion) {
        onExpandedChange?.(() => ({
          [rowId]: true,
        }));
      }
      onRowClick?.(rowId, event);
    },
    [enableRowExpansion, onExpandedChange, onRowClick]
  );

  // Set up virtualization
  // Fairly naive implementation, when real use-cases emerge, re-visit.
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    estimateSize: React.useCallback(() => virtualRowSize, [virtualRowSize]),
    overscan: 10,
  });

  const { virtualItems: virtualRows, totalSize } = rowVirtualizer;
  const useVirtualization = virtualization && !!virtualContainerHeight;

  if (virtualization && !virtualContainerHeight) {
    console.warn(
      '⚠️ You MUST set `virtualContainerHeight` in px when using `virtualization`. Virtualization is NOT ENABLED with current props'
    );
  }

  // Virtualization needs to know how much space to add before and after
  const paddingTop = useVirtualization && virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    useVirtualization && virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  // Data set differs based on virtualization
  const rowsToRender = useVirtualization ? virtualRows : rows;

  return (
    <div
      ref={tableContainerRef}
      style={useVirtualization ? { height: virtualContainerHeight, overflow: 'auto' } : undefined}
    >
      <Table
        style={{
          tableLayout: fixedLayout ? 'fixed' : undefined,
          ...(useVirtualization ? { height: `${totalSize}px`, width: '100%' } : undefined),
        }}
        zebraStripes={zebraStripes}
        verticalBorders={verticalBorders}
      >
        <Table.Thead sticky={stickyHeader}>
          {table.getHeaderGroups().map(headerGroup => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isString = typeof header.column.columnDef.header === 'string';
                const isProjectColumn = header.id === 'num_of_projects';
                return isString || isProjectColumn ? (
                  <Table.ThDefault<T>
                    key={header.id}
                    header={header}
                    columnResizeMode={columnResizeMode}
                    deltaOffset={table.getState().columnSizingInfo.deltaOffset}
                    leftOffset={leftOffsets[header.column.id]}
                    rightOffset={rightOffsets[header.column.id]}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.ThDefault>
                ) : (
                  <React.Fragment key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </React.Fragment>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {/* In case of virtualization, make scrollable space */}
          {useVirtualization && paddingTop > 0 && (
            <tr>
              <td style={{ height: `${paddingTop}px` }} />
            </tr>
          )}
          {rowsToRender.map(virtualRow => {
            const row = rows[virtualRow.index];
            return (
              <Table.Tr
                key={row.id}
                data-row-id={row.id}
                isSelected={row.getCanSelect() && row.getIsSelected()}
                isClickable={!!onRowClick}
                onClick={handleRowClick}
                highlight={highlightedRows.includes(row.id)}
              >
                {row.getVisibleCells().map(cell => {
                  const isPinned = cell.column.getIsPinned();
                  const meta = cell.column.columnDef.meta;
                  const isHighlighted = highlightedColumns.includes(cell.column.id);
                  return (
                    <Table.Td
                      key={cell.id}
                      pinnedLeft={isPinned === 'left'}
                      pinnedRight={isPinned === 'right'}
                      alignRight={!!meta?.alignRight}
                      numeric={!!meta?.numeric}
                      highlight={isHighlighted}
                      {...{
                        style: {
                          width: cell.column.getSize(),
                          left: isPinned === 'left' ? leftOffsets[cell.column.id] : undefined,
                          right: isPinned === 'right' ? rightOffsets[cell.column.id] : undefined,
                        },
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            );
          })}
          {/* In case of virtualization, make scrollaple space */}
          {useVirtualization && paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${paddingBottom}px` }} />
            </tr>
          )}
        </Table.Tbody>
        {hideFooter && aggregateFooter && (
          <Table.Tfoot sticky={stickyFooter} className={styles.aggregateRow}>
            {table.getFooterGroups().map(footerGroup => (
              <Table.Tr
                key={footerGroup.id}
                isSelected={rowSelection && Object.keys(rowSelection).length > 0}
              >
                {footerGroup.headers.map(header => {
                  const meta = header.column.columnDef.meta;
                  const isPinned = header.column.getIsPinned();
                  const isAttribute = header.id !== 'selection';
                  const isHighlighted = highlightedColumns.includes(header.id);
                  const isProjectColumn =
                    header.id === 'project' || header.id === 'num_of_projects';
                  return isAttribute ? (
                    <Table.Th
                      key={header.id}
                      alignRight={!!meta?.alignRight}
                      pinnedLeft={isPinned === 'left'}
                      pinnedRight={isPinned === 'right'}
                      numeric={!!meta?.numeric}
                      highlight={isHighlighted}
                      style={{
                        width: header.column.getSize(),
                        left: isPinned === 'left' ? leftOffsets[header.column.id] : undefined,
                        right: isPinned === 'right' ? rightOffsets[header.column.id] : undefined,
                        ...(isProjectColumn ? { fontVariantNumeric: 'normal' } : {}),
                      }}
                    >
                      {!meta?.excludeAggregateView &&
                        (meta?.readOnlyAggregateView ? (
                          <AggregateValue
                            column={header.column}
                            rows={rowsToRender}
                            selectedFunction={
                              selectedAggregateFunctions?.[header.id] ||
                              (meta?.numeric ? 'sum' : 'uniqueCount')
                            }
                            rowSelection={rowSelection || {}}
                            aggregateFunctions={aggregateFunctions || {}}
                            alignRight={!!meta?.alignRight}
                          />
                        ) : (
                          <AggregateDropdown
                            selectedFunction={
                              selectedAggregateFunctions?.[header.id] ||
                              (meta?.numeric ? 'sum' : 'uniqueCount')
                            }
                            onSelect={(value: any) => handleAggregateSelection?.(header.id, value)}
                            numeric={!!meta?.numeric}
                            alignRight={!!meta?.alignRight}
                            mandatory={!!meta?.mandatory}
                            rows={rowsToRender}
                            rowSelection={rowSelection}
                            column={header.column}
                            aggregateFunctions={aggregateFunctions || {}}
                          />
                        ))}
                    </Table.Th>
                  ) : (
                    <Table.Th key={header.id}></Table.Th>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tfoot>
        )}
        {!hideFooter && (
          <Table.Tfoot sticky={stickyFooter}>
            {table.getFooterGroups().map(footerGroup => (
              <Table.Tr key={footerGroup.id}>
                {footerGroup.headers.map(header => {
                  const isHighlighted = highlightedColumns.includes(header.id);
                  const isProjectColumn =
                    header.id === 'project' || header.id === 'num_of_projects';
                  return (
                    <Table.Th
                      key={header.id}
                      highlight={isHighlighted}
                      style={isProjectColumn ? { fontVariantNumeric: 'normal' } : undefined}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.footer, header.getContext())}
                    </Table.Th>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tfoot>
        )}
      </Table>
    </div>
  );
};
DataTable.displayName = 'DataTable';
